import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { Plus, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import {
  canAuthorInsightReports,
  canViewInsightReports,
} from "@/lib/insightReportsAccess";
import { insightReportsApi } from "@/services/api/insightReports";

export const Route = createFileRoute("/portal/knowledgebase-sql/insight-reports/")({
  component: InsightReportsList,
});

function InsightReportsList() {
  const { token } = useAuth();
  const { isOwner, isAdmin, isPlatformAdmin } = usePermissions();
  const canView = canViewInsightReports({ isOwner, isAdmin, isPlatformAdmin });
  const canAuthor = canAuthorInsightReports({ isOwner, isAdmin, isPlatformAdmin });

  const reportsQuery = useQuery({
    queryKey: ["insight-reports-list"],
    queryFn: () => insightReportsApi.list(token ?? ""),
    enabled: !!token && canView,
  });

  if (!canView) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">
          Insight Reports isn't available for your account yet.
        </p>
      </div>
    );
  }

  const reports = reportsQuery.data ?? [];

  return (
    <div className="space-y-6 p-6">
      <Helmet>
        <title>Insight Reports</title>
      </Helmet>
      <PageHeader
        title="Insight Reports"
        description="Chat-authored reports that re-run and re-narrate themselves every time you open them."
      >
        {canAuthor && (
          <Button asChild>
            <Link to="/portal/knowledgebase-sql/insight-reports/new">
              <Plus className="mr-1.5 h-4 w-4" />
              New report
            </Link>
          </Button>
        )}
      </PageHeader>

      {reportsQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : reports.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed p-10 text-center">
          <Sparkles className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            No Insight Reports yet.
          </p>
        </div>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {reports.map((r) => (
            <li key={r.slug}>
              <Link
                to="/portal/knowledgebase-sql/insight-reports/$slug"
                params={{ slug: r.slug }}
                className="block rounded-lg border p-4 hover:bg-muted/50"
              >
                <p className="font-medium">{r.title}</p>
                {r.description && (
                  <p className="mt-1 text-sm text-muted-foreground">{r.description}</p>
                )}
                <p className="mt-2 text-xs text-muted-foreground">
                  {r.section_count} section{r.section_count === 1 ? "" : "s"}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
